import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Award, MapPin, Calendar, ArrowRight } from "lucide-react";
import { PETITION_URL } from "../lib/constants";
import Reveal from "./Reveal";

/**
 * SponsorEvent
 *
 * Announcement section: JC Law Foundation is a proud sponsor of the
 * 6th Annual National CPR & AED Awareness Rally & March,
 * July 23–25, 2026, Washington, D.C.
 */
const SponsorEvent = () => {
    return (
        <section
            id="sponsor-event"
            data-testid="sponsor-event"
            className="relative bg-navy-900 text-editorial py-20 md:py-28 overflow-hidden"
            aria-labelledby="sponsor-event-heading"
        >
            <div className="absolute inset-0 bg-noise opacity-25 pointer-events-none" />
            {/* Ambient coral glow */}
            <motion.div
                className="absolute -top-32 -left-24 w-[520px] h-[520px] rounded-full bg-coral/15 blur-3xl pointer-events-none"
                animate={{ scale: [1, 1.1, 1], opacity: [0.35, 0.55, 0.35] }}
                transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
            />

            <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
                {/* Left: Ribbon / Badge */}
                <Reveal className="lg:col-span-4 flex justify-center lg:justify-start">
                    <div className="relative">
                        {/* Ribbon */}
                        <div className="relative inline-flex flex-col items-center">
                            <div className="relative bg-coral text-white px-8 py-6 shadow-2xl">
                                <div className="absolute inset-x-0 -top-3 h-3 bg-coral-dark/50" aria-hidden />
                                <div className="flex items-center gap-3">
                                    <Award className="w-8 h-8" strokeWidth={1.8} />
                                    <div>
                                        <p className="text-[10px] uppercase tracking-[0.28em] text-white/85 leading-none mb-1.5">
                                            2026
                                        </p>
                                        <p className="font-serif italic text-2xl leading-none">
                                            Proud Sponsor
                                        </p>
                                    </div>
                                </div>
                            </div>
                            {/* Ribbon tails */}
                            <div className="flex w-full">
                                <div className="w-1/2 h-6 bg-coral" style={{ clipPath: "polygon(0 0, 100% 0, 100% 100%, 50% 60%, 0 100%)" }} aria-hidden />
                                <div className="w-1/2 h-6 bg-coral" style={{ clipPath: "polygon(0 0, 100% 0, 100% 100%, 50% 60%, 0 100%)" }} aria-hidden />
                            </div>
                            <div className="mt-4 text-[10px] uppercase tracking-[0.22em] text-gold text-center">
                                JC Law Foundation
                            </div>
                        </div>
                    </div>
                </Reveal>

                {/* Right: Copy */}
                <div className="lg:col-span-8">
                    <Reveal>
                        <p className="overline mb-5 text-gold flex items-center gap-2">
                            <span className="inline-block w-2 h-2 rounded-full bg-coral animate-heartbeat" />
                            Upcoming Event · We&rsquo;re a Sponsor
                        </p>
                    </Reveal>
                    <Reveal delay={0.05}>
                        <h2
                            id="sponsor-event-heading"
                            className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-balance"
                        >
                            JC Law Foundation is a <em className="text-gold not-italic italic">Proud Sponsor</em>.
                        </h2>
                    </Reveal>
                    <Reveal delay={0.15}>
                        <p className="mt-6 text-lg md:text-xl text-editorial/85 leading-relaxed max-w-2xl">
                            The JC Law Foundation is proud to sponsor the <strong>6th Annual National CPR & AED Awareness Rally & March</strong>, taking place July 23–25, 2026, in Washington, D.C.
                        </p>
                        <p className="mt-4 text-base md:text-lg text-editorial/75 leading-relaxed max-w-2xl">
                            We invite attendees to stop by our booth to learn more about the Hotel Cardiac Emergency Preparedness Act, sign the petition, and support our mission to improve cardiac emergency preparedness in hotels across the country.
                        </p>
                    </Reveal>

                    <Reveal delay={0.25}>
                        <div className="mt-8 flex flex-wrap gap-x-8 gap-y-4 text-sm">
                            <div className="flex items-center gap-2.5 text-editorial/90">
                                <Calendar className="w-4 h-4 text-gold" aria-hidden />
                                <span>July 23–25, 2026</span>
                            </div>
                            <div className="flex items-center gap-2.5 text-editorial/90">
                                <MapPin className="w-4 h-4 text-gold" aria-hidden />
                                <span>Washington, D.C.</span>
                            </div>
                            <div className="flex items-center gap-2.5 text-editorial/90">
                                <Award className="w-4 h-4 text-gold" aria-hidden />
                                <span>6th Annual National CPR & AED Awareness Rally & March</span>
                            </div>
                        </div>
                    </Reveal>

                    <Reveal delay={0.35}>
                        <div className="mt-10 flex flex-col sm:flex-row gap-3 sm:gap-4">
                            <Link
                                to="/our-story"
                                data-testid="sponsor-learn-btn"
                                className="group inline-flex items-center justify-center gap-2 px-7 py-4 bg-editorial text-navy-900 hover:bg-gold hover:text-navy-900 font-medium transition-all duration-300"
                            >
                                Learn More About Our Mission
                                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                            </Link>
                            <a
                                href={PETITION_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="sponsor-petition-btn"
                                className="group inline-flex items-center justify-center gap-2 px-7 py-4 bg-coral hover:bg-coral-dark text-white font-semibold transition-colors duration-300"
                            >
                                Sign the Petition
                                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                            </a>
                        </div>
                    </Reveal>
                </div>
            </div>
        </section>
    );
};

export default SponsorEvent;
